import { observable, computed, makeObservable } from 'mobx';

import { EntityList, Vehicle } from '@vromo/models';
import { Job } from '@vromo/models';
import { RootStore } from './RootStore';

import sortBy from 'lodash/sortBy';

export const MAX_FILTERED_JOBS = 1000;

export default class JobStore {
    constructor(context: RootStore) {
        makeObservable(this, {
            jobs: observable,
            loaded: observable,
            byVehicle: computed,
            filteredJobs: computed,
            open: computed,
            underway: computed,
            atRisk: computed,
            unassigned: computed
        });

        this.rootStore = context;
    }

    rootStore: RootStore;

    jobs: EntityList<Job> = new EntityList(Job);
    loaded = false;

    withId = (id: number) => this.jobs._map.get(id);

    withVehicle = (id: number) => this.byVehicle.get(id) || [];

    get byVehicle() {
        const map = new Map<number, Array<Job>>();

        this.open.forEach((job) => {
            if (job.vehicle) {
                const entry = map.get(job.vehicle);

                if (entry) {
                    entry.push(job);
                } else {
                    map.set(job.vehicle, [job]);
                }
            }
        });
        return map;
    }

    get filteredJobs(): Job[] {
        const { groupStore, viewStore, vehicleStore } = this.rootStore;
        const filters = viewStore.filters.jobs;

        return sortBy(
            this.jobs.filter((it: Job) => {
                const vehicle = vehicleStore.withId(it.vehicle!)!;
                return (
                    statusFilter(it, filters.status) &&
                    searchFilter(it, vehicle, filters.search) &&
                    teamsFilter(it, filters.teams) &&
                    vehiclesFilter(it, filters.vehicles) &&
                    sitesFilter(it, filters.sites) &&
                    policiesFilter(it, filters.policies) &&
                    dateFilter(it, filters.startDate, filters.endDate)
                );
            }),
            (it) => {
                switch (filters.order) {
                    case 'name':
                        return it.name?.trim().toLowerCase();
                    case 'created':
                        return it.created ? it.created * -1 : -Infinity;
                    case 'deadline':
                        return it.nextDeadline;
                    case 'group': {
                        const group = it.group && groupStore.withId(it.group);
                        return group ? group.name?.trim().toLowerCase() : '';
                    }
                    case 'risk':
                    default:
                        return it.risk;
                }
            }
        ).slice(0, MAX_FILTERED_JOBS);
    }

    get open() {
        return this.jobs.filter((it) => it.status !== 'cancelled' && it.status !== 'completed');
    }

    get underway() {
        return this.jobs.filter((it) => it.status === 'underway');
    }

    get atRisk() {
        return this.jobs.filter((it) => it.risk < 0.5);
    }

    get unassigned() {
        return this.jobs.filter((it) => it.status === 'open');
    }
}

const sitesFilter = (job: Job, sites: number[]) =>
    sites.length ? sites.some((s) => s === job.site?.id) : true;

const policiesFilter = (job: Job, policies: number[]) =>
    policies.length ? policies.some((p) => p === job?.policy?.id) : true;

const statusFilter = (job: Job, filter: string) => {
    switch (filter) {
        case 'active':
            return job.status !== 'completed' && job.status !== 'cancelled';
        case 'open':
            return job.status === 'open';
        case 'with_driver':
            return job.status === 'dispatched' || job.status === 'underway';
        case 'dispatched':
            return job.status === 'dispatched';
        case 'underway':
            return job.status === 'underway';
        case 'completed':
            return job.status === 'completed';
        case 'cancelled':
            return job.status === 'cancelled';
        case 'all':
        default:
            return true;
    }
};

const searchFilter = (job: Job, vehicle: Vehicle, search: string) => {
    const { tasks, name, id } = job;
    const term = search.toLowerCase();

    if (term.length === 0) return true;

    const vehicleMatches = vehicle?.driver?.displayName?.toLowerCase().includes(term);

    const taskMatches = tasks.reduce((matches, task) => {
        if (task.zone && task.zone.name.toLowerCase().includes(term)) return true;
        if (task.zone && task.zone.address.toLowerCase().includes(term)) return true;

        return matches;
    }, false);

    const jobNameMatches = name.toLowerCase().includes(term);
    const jobIdMatches = parseInt(term) !== NaN ? id === parseInt(term) : false;

    return term ? taskMatches || jobNameMatches || jobIdMatches || vehicleMatches : true;
};

const teamsFilter = (job: Job, teams: number[]) =>
    teams.length ? teams.some((t) => t === job.group) : true;

const vehiclesFilter = (job: Job, vehicles: number[]) =>
    vehicles.length ? vehicles.some((v) => v === job.vehicle) : true;

const dateFilter = (job: Job, startDate: number | null, endDate: number | null) => {
    if (startDate && endDate) {
        return (
            !job.nextDeadline ||
            (job.nextDeadline > startDate.valueOf() && job.nextDeadline < endDate.valueOf())
        );
    } else {
        return true;
    }
};
